import {
  Breadcrumb,
  Button,
  Input,
  message,
  PageHeader,
  Select,
  Form,
  Row,
  Col,
  Checkbox,
} from "antd";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Page from "_components/Page";
import { companyService, ApplicationDocumentService } from "_services";
import TabMenu from "../TabMenu";
import { TiepDocumentType, tiepApplicationStatuses } from "_helpers/enums";

function Add({ history, match }) {
  const { id } = match.params;
  const [isLoading, setIsLoading] = useState(false);
  const [company, setCompany] = useState({});
  const [base64File, setBase64File] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);
  const [form] = Form.useForm();
  const { Option } = Select;

  useEffect(() => {
    setIsLoading(true);
    companyService.getById(id).then((x) => {
      setCompany(x.content);
      setIsLoading(false);
      
      // Set default form values
      form.setFieldsValue({
        applicationStatus: "APPLICATION"
      });
    });
  }, [id]);

  const handleFormSubmit = () => {
    // Check if file is selected before validating form
    if (!originalFileName) {
      message.error("Please select a file to upload");
      return;
    }

    form
      .validateFields()
      .then((values) => {
        setIsLoading(true);
        
        const payload = {
          documentName: values.documentName || originalFileName,
          applicationStatus: values.applicationStatus,
          applicationDocumentType: values.applicationDocumentType,
          checked: values.checked || false,
          comment: values.comment,
          base64FileString: base64File,
          originalFileName: originalFileName,
        };

        console.log("Document payload:", payload);
        
        ApplicationDocumentService.uploadSubDocument(id, payload)
          .then((response) => {
            setIsLoading(false);
            message.success("Document uploaded successfully!");
            history.push(".");
          })
          .catch((error) => {
            setIsLoading(false);
            console.error("Upload error:", error);
            
            // Handle different types of errors
            if (error.response) {
              // Server responded with error status
              const status = error.response.status;
              const errorMessage = error.response.data?.message || error.message;
              
              if (status === 400) {
                message.error(`Invalid request: ${errorMessage}`);
              } else if (status === 413) {
                message.error("File size too large. Please upload a smaller file.");
              } else if (status === 415) {
                message.error("Unsupported file type. Please upload a supported document format.");
              } else if (status === 500) {
                message.error("Server error. Please try again later.");
              } else {
                message.error(`Upload failed: ${errorMessage}`);
              }
            } else if (error.request) {
              // Network error
              message.error("Network error. Please check your connection and try again.");
            } else {
              // Other errors
              message.error(`Upload failed: ${error.message || 'Unknown error'}`);
            }
          });
      })
      .catch((error) => {
        console.log("Validation failed:", error);
        message.error("Please fill in all required fields");
      });
  };

  const onFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      const maxSize = 10 * 1024 * 1024; // 10MB limit
      
      // Validate file size
      if (file.size > maxSize) {
        message.error("File size must be less than 10MB");
        e.target.value = null;
        return;
      }
      
      setOriginalFileName(file.name);
      let reader = new FileReader();
      reader.onload = function (e) {
        setBase64File(reader.result);
      };
      reader.onerror = function (error) {
        message.error("Error reading file: " + error);
        setOriginalFileName(null);
        setBase64File(null);
      };
      reader.readAsDataURL(file);
      
      // Clear any validation errors for the file field
      form.setFields([{
        name: 'file',
        errors: [],
      }]);
    } else {
      setOriginalFileName(null);
      setBase64File(null);
    }
  };

  const onCancel = () => {
    history.push(".");
  };

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/companies">Company Search</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/companies/${company.id}/summary`}>
                Company: {company.code}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/companies/${id}/document-upload`}>
                Documents
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Add Document</Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={[
          <Button
            key="cancel"
            onClick={onCancel}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={isLoading}
            onClick={handleFormSubmit}
          >
            Save
          </Button>,
        ]}
      >
        <Page inner>
          <TabMenu menu="document-upload" id={id} />
          <Form
            form={form}
            name="form-create"
            layout="vertical"
            requiredMark={false}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="documentName"
                  label="Document Name"
                  rules={[{ required: true, message: "Document name is required" }]}
                >
                  <Input placeholder="Enter document name" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="applicationDocumentType"
                  label="Document Type"
                  rules={[{ required: true, message: "Document type is required" }]}
                >
                  <Select placeholder="Select document type" allowClear>
                    {TiepDocumentType.map((type) => (
                      <Option value={type.value} key={type.value}>
                        {type.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="applicationStatus"
                  label="Application Status"
                  rules={[{ required: true, message: "Application status is required" }]}
                >
                  <Select placeholder="Select application status" allowClear defaultValue="APPLICATION">
                    {tiepApplicationStatuses.map((status) => (
                      <Option value={status.value} key={status.value}>
                        {status.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="checked"
                  label="Checked"
                  valuePropName="checked"
                >
                  <Checkbox>Mark as checked</Checkbox>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="file"
                  label="File Upload"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!originalFileName) {
                          return Promise.reject(new Error('File is required'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    type="file"
                    name="file"
                    onChange={onFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  {originalFileName && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
                      Selected: {originalFileName}
                    </div>
                  )}
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="comment"
                  label="Comment"
                >
                  <Input.TextArea 
                    placeholder="Enter comments about the document" 
                    rows={4} 
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Page>
      </PageHeader>
    </div>
  );
}

export default Add;
